/**
 * RiskSourceDialog — inline PDF viewer that expands and highlights the exact
 * clause paragraph a Cognara risk was extracted from.  Renders the source
 * document via the backend `/documents/serve` endpoint and passes the risk's
 * `quotedText` to the browser's native PDF search (works in Chrome/Edge/Firefox
 * via `#search=` URL fragment).  When the referenced document can't be located
 * in the contract's uploads we still surface the clause reference + quote so
 * the user has full traceability metadata.
 */
import { useMemo, useEffect, useState } from 'react';
import { contractDocUrl } from '@/utils/contractDocUrl';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, ExternalLink, Quote, MapPin, AlertTriangle } from 'lucide-react';
import ClauseHighlightPdf from './ClauseHighlightPdf';

/** The page a clause reference cites: "p.10", "p 10", "page 10", "pp. 10-12". */
function citedPage(...refs) {
  for (const ref of refs) {
    const m = String(ref || '').match(/\bp(?:ages?|p?\.?)\s*([0-9]{1,4})/i);
    if (m) return parseInt(m[1], 10);
  }
  return 0;
}

const API = process.env.REACT_APP_BACKEND_URL; // eslint-disable-line no-unused-vars

/**
 * Find a document whose file_name best matches the risk's documentReference.
 * Falls back to the first uploaded doc so the viewer still opens something
 * useful when Cognara returns a generic filename.
 */
function resolveDocument(documents = [], reference = '') {
  if (!documents.length || !reference) return null;
  const target = reference.toLowerCase().trim();
  const exact = documents.find(d => (d.file_name || '').toLowerCase() === target);
  if (exact) return exact;
  const partial = documents.find(d => {
    const name = (d.file_name || '').toLowerCase();
    return name.includes(target) || target.includes(name.replace(/\.[a-z0-9]+$/, ''));
  });
  return partial || null;
}

export function RiskSourceDialog({ open, onOpenChange, risk, contractId, documents = [] }) {
  const doc = useMemo(() => {
    const ref = risk?.documentReference || '';
    if (!ref) return null;
    const target = ref.toLowerCase().trim();
    const exact = documents.find(d => (d.file_name || '').toLowerCase() === target);
    if (exact) return exact;
    const partial = documents.find(d => {
      const name = (d.file_name || '').toLowerCase();
      return name.includes(target) || target.includes(name.replace(/\.[a-z0-9]+$/, ''));
    });
    return partial || null;   // don't silently fall back to documents[0] — misleading provenance
  }, [documents, risk]);
  const isPdf = (doc?.mime_type || '').includes('pdf') || (doc?.file_name || '').toLowerCase().endsWith('.pdf');

  // Native PDF viewers accept `#search=` (Chromium) and `#page=` fragments.
  // Encoding the quoted text (trimmed to first ~10 words to survive the fragment)
  // makes Chrome / Edge scroll to the match and highlight it automatically.
  const searchTerm = useMemo(() => {
    if (!risk?.quotedText) return '';
    const words = String(risk.quotedText).replace(/["“”]/g, '').split(/\s+/).slice(0, 10).join(' ');
    return encodeURIComponent(words);
  }, [risk]);

  const citedHint = useMemo(
    () => citedPage(risk?.page, risk?.clauseReference, risk?.documentReference),
    [risk]);
  const [locatedPage, setLocatedPage] = useState(0);
  const hintPage = locatedPage || citedHint;

  // The page is read out of the banked reading of the document, which keeps a
  // marker for every page it transcribed — the only way to land on the right
  // page of a scan, where the PDF itself has no text to search.
  useEffect(() => {
    let dead = false;
    setLocatedPage(0);
    const name = doc?.file_name;
    if (!open || !contractId || !name || !risk?.quotedText) return undefined;
    api.get(`/contracts-ai/contracts/${contractId}/locate-quote`,
      { params: { file_name: name, quote: String(risk.quotedText).slice(0, 400) } })
      .then(({ data }) => { if (!dead && data?.page > 0) setLocatedPage(data.page); })
      .catch(() => {});
    return () => { dead = true; };
  }, [open, contractId, doc, risk]);

  const [pdfUrl, setPdfUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPdfUrl(null);
    if (!open || !doc || !contractId) return undefined;
    const fragment = `#page=${hintPage || 1}${searchTerm ? `&search=${searchTerm}` : ''}`;
    contractDocUrl(contractId, doc.storage_path || doc.file_url, fragment)
      .then(u => { if (!cancelled) setPdfUrl(u); })
      .catch(() => { if (!cancelled) setPdfUrl(null); });
    return () => { cancelled = true; };
  }, [open, doc, contractId, searchTerm, hintPage]);

  if (!risk) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[92vw] h-[88vh] flex flex-col p-0 gap-0"
        data-testid="risk-source-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4 text-purple-500" />
            Risk Source · <span className="font-mono text-xs text-muted-foreground">{risk.id}</span>
            <span className="text-foreground">{risk.title}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Exact clause paragraph that established this risk, opened inline in the source document.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[minmax(280px,320px)_1fr] overflow-hidden">
          {/* Left — traceability metadata + quoted text */}
          <div className="border-r border-border bg-muted/30 overflow-y-auto p-4 space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Document</p>
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm text-foreground break-words">{doc?.file_name || risk.documentReference || 'Not linked'}</p>
                  {doc && (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-purple-500 hover:text-purple-400 inline-flex items-center gap-1 mt-1"
                      data-testid="risk-source-open-new-tab"
                    >
                      Open in new tab <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {risk.clauseReference && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Clause</p>
                <Badge variant="outline" className="text-xs font-mono">{risk.clauseReference}</Badge>
                {hintPage > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1" data-testid="risk-source-cited-page">
                    {locatedPage ? `found on page ${hintPage} of this file` : `cites page ${hintPage}`}
                  </p>
                )}
              </div>
            )}

            {risk.quotedText && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                  <Quote className="w-3 h-3" /> Verbatim Extract
                </p>
                <blockquote
                  className="text-xs italic leading-relaxed rounded-md border-l-4 border-purple-500 bg-purple-500/10 p-3 text-foreground"
                  data-testid="risk-source-quote"
                >
                  “{risk.quotedText}”
                </blockquote>
              </div>
            )}

            <div className="pt-2 border-t border-border">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Category · Priority</p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-[10px]">{risk.category || 'GENERAL'}</Badge>
                <Badge variant="outline" className="text-[10px]">{risk.priority || '—'}</Badge>
                <Badge variant="outline" className="text-[10px]">Score {risk.score ?? '—'}</Badge>
              </div>
            </div>

            {risk.exposure && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Exposure</p>
                <p className="text-xs text-foreground">{risk.exposure}</p>
              </div>
            )}

            {risk.mitigation && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recommended Mitigation</p>
                <p className="text-xs text-foreground">{risk.mitigation}</p>
              </div>
            )}

            {!doc && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-600 dark:text-amber-400 flex gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>
                  This risk didn't reference a stored document.  The clause reference and verbatim
                  extract are shown above so you can locate the paragraph manually.
                </span>
              </div>
            )}
          </div>

          {/* Right — inline PDF viewer / document preview */}
          <div className="bg-background overflow-hidden">
            {doc && isPdf && pdfUrl ? (
              <div className="w-full h-full" data-testid="risk-source-pdf">
                <ClauseHighlightPdf
                  key={pdfUrl}
                  url={pdfUrl}
                  quote={risk.quotedText}
                  hintPage={hintPage}
                  fileName={doc.file_name}
                />
              </div>
            ) : doc ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 p-8 text-center">
                <FileText className="w-10 h-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Inline preview isn't available for <span className="font-medium">{doc.file_name}</span>.
                </p>
                <Button asChild size="sm" variant="outline">
                  <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="risk-source-download">
                    Download / Open <ExternalLink className="w-3.5 h-3.5 ml-1" />
                  </a>
                </Button>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No source document uploaded for this contract yet.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RiskSourceDialog;
